import { exercise, isNull, routine } from "@gymlog/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)              │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for GymLog's trash-enabled (L2) tables. Every live read
 * of `exercise` / `routine` composes these predicates (the builders in
 * `gym/queries.ts` import them from here). The ONLY reads that intentionally see deleted
 * rows are the trash (`getTrashedExercisesQuery` /
 * `getTrashedRoutinesQuery`) and some harmless id-scoped lookups, listed in
 * the guard's whitelist (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant was the #1 bug family of the
 * SOFT_DELETE campaign (⌘K and `checkUnsyncedActor` ignoring `deleted_at`;
 * derivatives of gym/meal/cash-sync that counted deleted entities). With a single choke
 * point + anti-regression guard, adding a raw read of these tables
 * outside the whitelist BREAKS the build (see
 * `live/__tests__/live-access-guard.test.ts`).
 *
 * ─── Cohorts (GymLog) ───────────────────────────────────────────────────────
 * SCHEMA REALITY (00048_gym_trash.sql): the ONLY user-domain tables with
 * a `deleted_at` trash field are `exercise` and `routine`, and BOTH are
 * roots (top-level). There is no logical FK "trash child → trash parent"
 * BETWEEN them: `routine_exercise` (the routine↔exercise pivot) does NOT
 * have `deleted_at` (an L1 fact), so the POS pilot's
 * `NOT EXISTS(parent in trash)` cohort does not apply. The invariant here is only each root's
 * own `deleted_at IS NULL`.
 *
 * CC-33 (DO NOT go back): `routine_version` is server-materialized and immutable
 * (FK RESTRICT from `workout_session.routine_version_id`); it is NOT in
 * `USER_DATA_TABLES` or the live/purge layer. Do not add it here.
 */

/** Live exercises: only their own `deleted_at IS NULL`. */
export function liveExerciseFilters() {
  return [isNull(exercise.deleted_at)];
}

/** Live routines: only their own `deleted_at IS NULL`. */
export function liveRoutineFilters() {
  return [isNull(routine.deleted_at)];
}
